<script setup lang="ts">
import { bindField, revealErrors } from '#core/utils/bind-field'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { ArrowLeftIcon } from '@heroicons/vue/24/outline'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { z } from 'zod'

interface AdvisorRequest {
  email: string
  firstName: string
  lastName: string
  organizationName: string
  organizationWebsite: string
}

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'success', payload: AdvisorRequest): void
}>()

const schema = z.object({
  email: z.string().trim().email('Enter a valid email'),
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  organizationName: z.string().trim().min(1, 'Organization name is required'),
  organizationWebsite: z.union([z.literal(''), z.string().trim().url('Enter a valid URL')]),
})

const form = useForm({
  defaultValues: {
    email: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    organizationWebsite: '',
  } satisfies AdvisorRequest,
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  onSubmitInvalid: ({ formApi }) => revealErrors(formApi),
  validators: { onDynamic: schema },
  onSubmit: ({ value }) => sendRequest(schema.parse(value)),
})

const errorMessage = ref<string | null>(null)
const loading = ref(false)

async function sendRequest(advisorForm: AdvisorRequest) {
  errorMessage.value = null
  loading.value = true
  try {
    await apiFetch('/advisor/join-requests', {
      method: 'post',
      params: { body: { ...advisorForm } },
    })
    emit('success', { ...advisorForm })
  }
  catch {
    errorMessage.value = 'Something went wrong submitting your request. Please try again.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <Button variant="outline" size="sm" class="self-start" @click="emit('back')">
      <ArrowLeftIcon aria-hidden="true" />
      Back
    </Button>

    <div>
      <h1 class="text-lg font-semibold text-primal">
        Forgd for Web3 Advisory Firms
      </h1>
      <p class="mt-1 text-sm text-secondary">
        Streamline your workflow today. Share your details and our team will contact you to
        configure your workspace. Free to use.
      </p>
    </div>

    <form class="space-y-3" novalidate @submit.prevent="form.handleSubmit()">
      <form.Field v-slot="{ field }" name="email">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Email
          </FieldLabel>
          <FieldContent>
            <Input
              v-bind="bindField(field).control"
              :name="field.name"
              type="text"
              size="md"
              placeholder="e.g., youruser@email.com"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>

      <div class="grid grid-cols-2 gap-3">
        <form.Field v-slot="{ field }" name="firstName">
          <Field>
            <FieldLabel v-bind="bindField(field).label">
              First name
            </FieldLabel>
            <FieldContent>
              <Input
                v-bind="bindField(field).control"
                :name="field.name"
                type="text"
                size="md"
                placeholder="e.g., Alice"
              />
              <FieldError v-bind="bindField(field).error" />
            </FieldContent>
          </Field>
        </form.Field>
        <form.Field v-slot="{ field }" name="lastName">
          <Field>
            <FieldLabel v-bind="bindField(field).label">
              Last name
            </FieldLabel>
            <FieldContent>
              <Input
                v-bind="bindField(field).control"
                :name="field.name"
                type="text"
                size="md"
                placeholder="e.g., Adams"
              />
              <FieldError v-bind="bindField(field).error" />
            </FieldContent>
          </Field>
        </form.Field>
      </div>

      <form.Field v-slot="{ field }" name="organizationName">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Name of your organization
          </FieldLabel>
          <FieldContent>
            <Input
              v-bind="bindField(field).control"
              :name="field.name"
              type="text"
              size="md"
              placeholder="e.g., Acme Advisory"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>

      <form.Field v-slot="{ field }" name="organizationWebsite">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Organization website (optional)
          </FieldLabel>
          <FieldContent>
            <Input
              v-bind="bindField(field).control"
              :name="field.name"
              type="url"
              size="md"
              placeholder="e.g., https://acme.xyz"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>

      <div v-if="errorMessage" class="mt-1 text-sm text-error">
        {{ errorMessage }}
      </div>

      <Button
        size="lg"
        class="h-12"
        block
        type="submit"
        :loading="loading"
      >
        Send Request
      </Button>
    </form>
  </div>
</template>
